/**
 * previews.js — Carousel Composer slide-cell previews, made in the browser.
 *
 * Tiles, the filmstrip, the tray and the slide strip use the 480px thumbnail.
 * A slide cell needs more than that, so the first time a photo is placed in a
 * slide a ~1600px-wide JPEG is decoded from the original and cached. Nothing is
 * uploaded and nothing touches disk. Cells show the thumbnail until the preview
 * is ready; framing uses the photo's w/h, so it is identical at any size.
 *
 * THE WHOLE LOOK IS BAKED IN HERE — the turn (see look.js) and the adjustments
 * (pipeline.js). That is why a cell stays a plain CSS background layer: no cell
 * owns a canvas, and no cell owns a WebGL context. The cache key is therefore the
 * full look, not just the rotation.
 *
 * Two caches, because a slider drag must not re-download anything:
 *   bases  — id@bakeKey -> the turned, downscaled, UNGRADED pixels (a canvas or a
 *            bitmap). Small LRU; recoverable by refetching.
 *   cache  — the full look key -> an object URL of the graded result. A drag
 *            re-grades from the base, which is one GPU pass, not a decode.
 * While the new grade is rendering a cell keeps the previous grade of the same
 * photo rather than dropping to a 480px thumbnail.
 *
 * Shoot photos come from another origin (media.hd.pics, which allows it) and
 * arrive with only the thumbnail's aspect, so their real size is measured here on
 * the first decode and handed back through onDims. Those delivery URLs rotate, so
 * a failed fetch is retried through the record's same-origin fallback.
 *
 * Module-level cache: previews survive leaving the page and coming back, like the
 * photos themselves, until a full reload.
 */
import { ADJ_KEYS, bakeKey, bakedBox, drawBaked, isNeutralAdj, normalizeAdj, normalizeLook } from './look.js';
import { renderLook } from './pipeline.js';

export const PREVIEW_WIDTH = 1600;
const CONCURRENCY = 2;
const MAX_BASES = 4;        // each base is a full preview-sized surface
const MAX_PER_FAMILY = 3;   // graded results kept per photo+turn

const cache = new Map();     // full look key -> object URL (or the original URL when it is small already)
const graded = new Set();    // the object URLs this module made, and may revoke
const family = new Map();    // id@bakeKey -> [full look key] oldest first
const bases = new Map();     // id@bakeKey -> { src, w, h, url } (LRU: last is newest)
const pending = new Map();   // id@bakeKey -> Promise of the base
const queued = new Set();
const queue = [];
const listeners = new Set();
let active = 0;
let version = 0;

/** id + turn: everything that shares one set of baked pixels. */
function famKey(id, look) {
  return id + '@' + bakeKey(look);
}

// Adjustments have to be in the key now that they are baked in. look.js's
// lookKey() is the same idea, but this one is built from ADJ_KEYS so a slider
// added later cannot quietly share a cache entry with the old one.
function adjKey(look) {
  const a = normalizeAdj(look && look.adj);
  return ADJ_KEYS.map((k) => a[k]).join(',');
}

// '#' rather than '|': bakeKey already contains a '|', so the family has to be
// split off the end, not the front.
export function previewKey(id, look) {
  return famKey(id, look) + '#' + adjKey(look);
}

function familyOf(key) {
  return key.slice(0, key.lastIndexOf('#'));
}

/**
 * The graded preview for this look, or — while it renders — the last grade of
 * the same photo and turn, which is a far better placeholder than the thumbnail.
 * Null when this photo has never been rendered.
 */
export function getPreview(id, look) {
  const exact = cache.get(previewKey(id, look));
  if (exact) return exact;
  const fam = family.get(famKey(id, look));
  return (fam && fam.length && cache.get(fam[fam.length - 1])) || null;
}

/** True when getPreview() is showing this exact look, not a stale one. */
export function hasPreview(id, look) {
  return cache.has(previewKey(id, look));
}

/**
 * The base pixels behind a preview: turned and downscaled, but ungraded, ready
 * for pipeline.renderLive() while a slider is moving. Null until one exists.
 */
export function getPreviewSource(id, look) {
  const b = bases.get(famKey(id, look));
  return b ? b.src : null;
}

export function previewsVersion() {
  return version;
}

export function subscribePreviews(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  version++;
  listeners.forEach((fn) => { fn(); });
}

function remember(key, url) {
  cache.set(key, url);
  const fk = familyOf(key);
  const list = family.get(fk) || [];
  const at = list.indexOf(key);
  if (at >= 0) list.splice(at, 1);
  list.push(key);
  while (list.length > MAX_PER_FAMILY) {
    const old = list.shift();
    const u = cache.get(old);
    cache.delete(old);
    // a cell may still be painting it this frame; let the repaint land first
    if (u && graded.has(u)) {
      graded.delete(u);
      setTimeout(() => URL.revokeObjectURL(u), 5000);
    }
  }
  family.set(fk, list);
}

function keepBase(fk, base) {
  bases.delete(fk);
  bases.set(fk, base);
  while (bases.size > MAX_BASES) {
    // Dropped, never close()d: getPreviewSource() hands these pixels to the
    // editor for renderLive, and a bitmap closed underneath it would throw
    // mid-drag. Letting go is enough — the collector takes it from here. The
    // object URL made from them stays valid and stays cached.
    bases.delete(bases.keys().next().value);
  }
}

// 1600px wide, but never shorter than a 3:2 frame at that width, so a panorama
// split across several slides keeps enough pixels in each part.
function previewWidth(w, h) {
  return Math.min(w, Math.max(PREVIEW_WIDTH, Math.round(PREVIEW_WIDTH / 1.5 * w / h)));
}

// Fetched as a blob rather than pointed at by an <img>, so a cross-origin shoot
// photo can never taint the canvas.
async function fetchBlob(im) {
  const get = async (u) => {
    const r = await fetch(u);
    if (!r.ok) throw new Error('http ' + r.status);
    return r.blob();
  };
  try {
    return await get(im.url);
  } catch (err) {
    if (!im.fallbackUrl) throw err;
    return get(im.fallbackUrl);   // the delivery URL rotated; go through the portal
  }
}

function blobImage(blob) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => { img._url = url; res(img); };
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error('decode')); };
    img.src = url;
  });
}

function release(source) {
  if (!source) return;
  if (source.close) source.close();
  if (source._url) URL.revokeObjectURL(source._url);
}

function toJpegUrl(canvas) {
  return new Promise((res) => {
    canvas.toBlob((b) => res(b ? URL.createObjectURL(b) : null), 'image/jpeg', 0.86);
  });
}

/** The turned, downscaled, ungraded pixels for one photo — decoded once. */
async function buildBase(im, l, onDims) {
  const blob = await fetchBlob(im);
  const srcW = im.srcW || im.w, srcH = im.srcH || im.h;
  let w = srcW, h = srcH, source = null;

  // Unknown size (a shoot photo), or a turn to bake: decode once at full size.
  if (im.exactDims === false || l.rot) {
    if (typeof createImageBitmap === 'function') {
      try { source = await createImageBitmap(blob); } catch { source = null; }
    }
    if (!source) source = await blobImage(blob);
    w = source.width || source.naturalWidth;
    h = source.height || source.naturalHeight;
    if (onDims && w && h) onDims(im.id, w, h);
  }

  const box = bakedBox(w, h, l);
  const tw = Math.max(1, Math.round(previewWidth(box.w, box.h)));
  const th = Math.max(1, Math.round(tw * box.h / box.w));

  if (!l.rot && tw >= w) {
    // The original is already small enough to paint. Keep its pixels for grading
    // and keep its own URL, so an untouched photo is never re-encoded.
    if (!source) {
      if (typeof createImageBitmap === 'function') {
        try { source = await createImageBitmap(blob); } catch { source = null; }
      }
      if (!source) source = await blobImage(blob);
    }
    return { src: source, w, h, url: im.url };
  }

  if (!source) {
    if (typeof createImageBitmap === 'function') {
      try {
        source = await createImageBitmap(blob, { resizeWidth: tw, resizeHeight: th, resizeQuality: 'high' });
      } catch { source = null; }
    }
    if (!source) source = await blobImage(blob);
  }

  const c = document.createElement('canvas');
  c.width = tw;
  c.height = th;
  const g = c.getContext('2d');
  g.imageSmoothingQuality = 'high';
  if (l.rot) drawBaked(g, source, w, h, l, c.width, c.height);
  else g.drawImage(source, 0, 0, c.width, c.height);
  release(source);
  return { src: c, w: tw, h: th, url: await toJpegUrl(c) };
}

function baseFor(im, l, onDims) {
  const fk = famKey(im.id, l);
  const have = bases.get(fk);
  if (have) {
    bases.delete(fk);
    bases.set(fk, have);   // touch: it is the newest again
    return Promise.resolve(have);
  }
  const inflight = pending.get(fk);
  if (inflight) return inflight;
  const p = buildBase(im, l, onDims).then((base) => {
    pending.delete(fk);
    keepBase(fk, base);
    return base;
  }, (err) => {
    pending.delete(fk);
    throw err;
  });
  pending.set(fk, p);
  return p;
}

async function renderPreview(im, look, onDims) {
  const l = normalizeLook(look);
  const base = await baseFor(im, l, onDims);
  if (isNeutralAdj(l.adj)) return { url: base.url, own: false };
  const out = renderLook(base.src, l, { width: base.w, height: base.h });
  if (!out) return { url: base.url, own: false };
  return { url: await toJpegUrl(out), own: true };
}

function pump() {
  while (active < CONCURRENCY && queue.length) {
    const { im, look, key, onDims } = queue.shift();
    active++;
    renderPreview(im, look, onDims)
      .then((out) => {
        if (!out || !out.url) return;
        if (out.own) graded.add(out.url);
        remember(key, out.url);
      })
      .catch(() => { /* the cell keeps its thumbnail */ })
      .finally(() => {
        active--;
        queued.delete(key);
        emit();
        pump();
      });
  }
}

// Ask for a preview of a placed photo, as it looks under `look`. Cheap to call on
// every render. onDims(id, w, h) reports the original's real size on first decode.
export function requestPreview(im, look, onDims) {
  if (!im) return;
  const key = previewKey(im.id, look);
  if (cache.has(key) || queued.has(key)) return;
  const l = normalizeLook(look);
  const plain = isNeutralAdj(l.adj);
  if (!l.rot && plain && im.exactDims !== false && previewWidth(im.w, im.h) >= im.w) {
    remember(key, im.url);
    emit();
    return;
  }
  // A drag leaves a trail of slider positions nobody will ever look at. Anything
  // for this photo that has not started yet is one of them.
  const fk = famKey(im.id, l);
  for (let i = queue.length - 1; i >= 0; i--) {
    if (familyOf(queue[i].key) === fk) {
      queued.delete(queue[i].key);
      queue.splice(i, 1);
    }
  }
  queued.add(key);
  queue.push({ im, look: l, key, onDims });
  pump();
}
